/**
 * StudentTimeBand.tsx
 * 학생 1명 하루 시간띠(단일 밴드) — 좌석 상세에서 사용.
 *
 * 시간이 단일 진실. 하나의 밴드에 교시 구분선/라벨 + 색 세그먼트 + 교시별출결 마크를 함께 표시.
 *  - 하늘색: 착석(present)  빨강: 미출석(absent)  회색: 예정(future)  노랑: 외출(outing)  빗금: 면제(exempt)
 *  - 교시(periods)는 표시용 구분선/라벨 (판정 아님)
 * 백엔드: GET /v1/owner/attendance-period-grid/time-band/student
 */

import React, { useEffect, useState } from 'react';
import { apiClient } from '../../../../services/authApi';
import { SEG_STYLE } from './AttendanceTimeBand';

interface Segment {
  start: string;
  end: string;
  state: string;
  start_min: number;
  end_min: number;
}

interface PeriodStatus {
  period_number?: number | null;
  period_name?: string | null;
  status: string;
}

interface BandPeriod {
  period_number?: number | null;
  period_name?: string | null;
  period_type?: string | null;
  start_time?: string;
  end_time?: string;
  start_min?: number | null;
  end_min?: number | null;
}

interface StudentBandResponse {
  student_id: string;
  name: string;
  date: string;
  now: string;
  now_min: number | null;
  day_start_min: number;
  day_end_min: number;
  periods: BandPeriod[];
  segments: Segment[];
  period_statuses?: PeriodStatus[];
  status: string;
  absence_minutes?: number | null;
  is_exempt: boolean;
  has_schedule: boolean;
}

interface StudentTimeBandProps {
  studentId?: string | null;
  targetDate?: string;
}

export const StudentTimeBand: React.FC<StudentTimeBandProps> = ({
  studentId,
  targetDate,
}) => {
  const [data, setData] = useState<StudentBandResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!studentId) {
      setData(null);
      return;
    }
    let cancelled = false;
    const fetchBand = async () => {
      setLoading(true);
      setError(null);
      try {
        const params: Record<string, string> = { student_id: studentId };
        if (targetDate) params.target_date = targetDate;
        const res = await apiClient.get(
          '/v1/owner/attendance-period-grid/time-band/student',
          { params },
        );
        if (!cancelled) setData(res.data);
      } catch (e: any) {
        if (!cancelled) {
          setError(
            e?.response?.data?.detail || e?.message || '시간띠 조회 실패',
          );
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchBand();
    return () => {
      cancelled = true;
    };
  }, [studentId, targetDate]);

  const span = data ? Math.max(1, data.day_end_min - data.day_start_min) : 1;
  const pct = (m: number): number => {
    if (!data) return 0;
    return ((m - data.day_start_min) / span) * 100;
  };
  const fmtMin = (m: number): string => {
    // 24시(1440분) 넘으면 익일로 표시 (예: 1490 → 익일 00:50)
    if (m >= 1440) {
      const mm = m - 1440;
      return `익일 ${String(Math.floor(mm / 60)).padStart(2, '0')}:${String(mm % 60).padStart(2, '0')}`;
    }
    return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  };

  if (loading) {
    return <div className="py-3 text-center text-xs text-gray-400">불러오는 중...</div>;
  }
  if (error) {
    return <div className="py-3 text-center text-xs text-red-500">⚠️ {error}</div>;
  }
  if (!data) return null;

  if (!data.has_schedule) {
    return (
      <div className="py-3 text-center text-xs text-gray-400">
        오늘 등록된 시간이 없습니다.
      </div>
    );
  }

  const showNow =
    data.now_min != null &&
    data.now_min >= data.day_start_min &&
    data.now_min <= data.day_end_min;

  return (
    <div style={{ width: '100%' }}>
      <div style={{ width: '100%' }}>
        {/* 밴드 본체 — 하나의 깔끔한 막대 (전체 폭 강제) */}
        <div
          className="relative h-11 rounded-lg border border-gray-300 bg-slate-200 overflow-hidden shadow-inner"
          style={{ width: '100%', minWidth: '100%', display: 'block' }}
        >
          {/* 세그먼트 (전체 높이) */}
          {data.segments.map((seg, i) => {
            const st = SEG_STYLE[seg.state] || SEG_STYLE.future;
            const left = pct(seg.start_min);
            const width = pct(seg.end_min) - left;
            return (
              <div
                key={`seg-${i}`}
                className={`absolute inset-y-0 ${st.className}`}
                style={{ left: `${left}%`, width: `${width}%`, ...st.style }}
                title={`${seg.start}~${seg.end} ${st.label}`}
              />
            );
          })}
          {/* 교시 구분선 — 흰색 반투명은 밝은 세그먼트 위에서 사실상 안 보였다.
              점선 + 진한 회색으로 바꿔 어느 색 위에서도 경계가 드러나게 한다. */}
          {data.periods.map((p, i) =>
            p.start_min != null && p.start_min > data.day_start_min ? (
              <div
                key={`div-${i}`}
                className="absolute inset-y-0 z-[3]"
                style={{
                  left: `${pct(p.start_min)}%`,
                  borderLeft: '1px dashed rgba(55,65,81,0.55)',
                }}
              />
            ) : null,
          )}
          {/* 교시 번호 — 구간이 좁으면 겹치니 폭이 충분할 때만 */}
          {data.periods.map((p, i) => {
            if (p.start_min == null || p.end_min == null || !p.period_number) return null;
            const left = pct(p.start_min);
            const width = pct(p.end_min) - left;
            if (width < 4) return null;
            return (
              <div
                key={`pnum-${i}`}
                className="absolute top-0 flex justify-center z-[4] pointer-events-none"
                style={{ left: `${left}%`, width: `${width}%` }}
              >
                <span className="text-[9px] leading-none mt-0.5 px-1 rounded bg-white/75 text-gray-600">
                  {p.period_number}
                </span>
              </div>
            );
          })}
          {/* 교시별출결 마크(○/×/△/□) 제거 — 출석부 좌석상세 띠에선 불필요(색 세그먼트로 충분) */}
          {/* 현재 시각 마커 */}
          {showNow && (
            <div
              className="absolute inset-y-0 w-0.5 bg-red-500 z-10"
              style={{ left: `${pct(data.now_min as number)}%` }}
            >
              <span className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-red-500" />
            </div>
          )}
        </div>

        {/* 시각 라벨 — 교시명 대신 시작·종료 시각만 간결 표시 (겹침 방지) */}
        <div className="flex justify-between items-center mt-1 text-[10px] text-gray-500">
          <span className="whitespace-nowrap">{fmtMin(data.day_start_min)}</span>
          <span className="whitespace-nowrap">{fmtMin(data.day_end_min)}</span>
        </div>

        {/* 범례 */}
        <div className="flex items-center gap-2 mt-1.5 text-[10px] text-gray-500 flex-wrap">
          {(['present', 'absent', 'future', 'outing', 'exempt'] as const).map((k) => (
            <span key={k} className="inline-flex items-center gap-0.5">
              <span
                className={`inline-block w-3 h-2.5 rounded-sm ${SEG_STYLE[k].className}`}
                style={SEG_STYLE[k].style}
              />
              {SEG_STYLE[k].label}
            </span>
          ))}
          {/* 밴드 바탕색(회색)이 무엇인지 안 적혀 있어 쉬는시간으로 오해받았다 */}
          <span className="inline-flex items-center gap-0.5">
            <span className="inline-block w-3 h-2.5 rounded-sm bg-slate-200 border border-gray-300" />
            등록 안 한 시간
          </span>
          <span className="ml-auto text-blue-600 font-medium">현재 {data.now}</span>
        </div>
      </div>
    </div>
  );
};

export default StudentTimeBand;
